import { UTCDate } from '@date-fns/utc';
import { addDays, getDay, getMonth, isSameDay } from 'date-fns';
import { CalendarDateRange, DateRangePickerVariant } from './types';
import {
    isDayDisabled,
    isWeekSelectable,
    normalizeDate,
    normalizeRange,
} from './utils';

export type RangeTone = 'selected' | 'preview' | null;

interface RangeMeta {
    isEndpointStart: boolean;
    isEndpointEnd: boolean;
    isSegmentStart: boolean;
    isSegmentEnd: boolean;
}

interface MatchedRange {
    range: CalendarDateRange;
    meta: RangeMeta;
}

export interface DayPresentation {
    tone: RangeTone;
    range: CalendarDateRange | null;
    isEndpoint: boolean;
    isSegmentStart: boolean;
    isSegmentEnd: boolean;
    isDisabled: boolean;
    isToday: boolean;
    isMainMonth: boolean;
}

interface GetDayPresentationOptions {
    day: UTCDate;
    month: number;
    variant?: DateRangePickerVariant;
    // The committed/selected ranges that should be rendered as real selections.
    periods: CalendarDateRange[];
    // A temporary range shown while the user is still hovering/selecting.
    previewPeriod?: CalendarDateRange | null;
    disableBefore?: UTCDate;
    disableAfter?: UTCDate;
    disabledDateKeys?: Set<number>;
    today: UTCDate;
}

function getWeekDayIndex(day: UTCDate): number {
    return (getDay(day) + 6) % 7;
}

function getRangeMeta(
    day: UTCDate,
    period: CalendarDateRange
): RangeMeta | null {
    const { start, end } = normalizeRange(period.start, period.end);
    if (day < start || day > end) {
        return null;
    }

    const weekDayIndex = getWeekDayIndex(day);
    return {
        isEndpointStart: day.getTime() === start.getTime(),
        isEndpointEnd: day.getTime() === end.getTime(),
        isSegmentStart: weekDayIndex === 0 || addDays(day, -1) < start,
        isSegmentEnd: weekDayIndex === 6 || addDays(day, 1) > end,
    };
}

function findRangeMeta(
    day: UTCDate,
    periods: CalendarDateRange[]
): MatchedRange | null {
    // `day` is always one concrete date cell in the calendar. We walk the
    // available periods to find the first range that contains that day.
    for (const period of periods) {
        const rangeMeta = getRangeMeta(day, period);

        if (rangeMeta) {
            return {
                range: period,
                meta: rangeMeta,
            };
        }
    }

    return null;
}

export function getDayPresentation({
    day,
    month,
    variant = 'range',
    periods,
    previewPeriod,
    disableBefore,
    disableAfter,
    disabledDateKeys,
    today,
}: GetDayPresentationOptions): DayPresentation {
    day = normalizeDate(day);
    const isToday = isSameDay(day, today);
    const isMainMonth = getMonth(day) === month;
    const isDisabledByBounds =
        variant === 'week'
            ? !isWeekSelectable(day, disableBefore, disableAfter)
            : isDayDisabled(day, disableBefore, disableAfter);
    const isDisabled =
        isDisabledByBounds || !!disabledDateKeys?.has(day.getTime());

    // First prefer a real selected period. Preview state should only be shown
    // for days that are not already part of one of the existing ranges.
    const selectedMatch = findRangeMeta(day, periods);

    if (selectedMatch) {
        return {
            tone: 'selected',
            range: selectedMatch.range,
            isEndpoint:
                !!selectedMatch.meta.isEndpointStart ||
                !!selectedMatch.meta.isEndpointEnd,
            isSegmentStart: selectedMatch.meta.isSegmentStart,
            isSegmentEnd: selectedMatch.meta.isSegmentEnd,
            isDisabled,
            isToday,
            isMainMonth,
        };
    }

    if (previewPeriod) {
        // Preview is the transient "where would this day end up" state while
        // dragging/hovering, so it uses the same range-meta logic as a real
        // period but with a different tone.
        const previewMeta = getRangeMeta(day, previewPeriod);

        if (previewMeta) {
            return {
                tone: 'preview',
                range: previewPeriod,
                isEndpoint:
                    !!previewMeta.isEndpointStart ||
                    !!previewMeta.isEndpointEnd,
                isSegmentStart: previewMeta.isSegmentStart,
                isSegmentEnd: previewMeta.isSegmentEnd,
                isDisabled,
                isToday,
                isMainMonth,
            };
        }
    }

    return {
        tone: null,
        range: null,
        isEndpoint: false,
        isSegmentStart: false,
        isSegmentEnd: false,
        isDisabled,
        isToday,
        isMainMonth,
    };
}
